import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plus, Users, Eye, Pencil, Trash2, UserPlus, Building2, Loader2, ShieldAlert } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DataTable, type ColumnDef, useDataTable } from '@/components/ui/data-table'
import { teamService, type Team } from '@/lib/api/services/teams'
import { useToast } from '@/components/ui/use-toast'
import { PermissionGate } from '@/components/auth/permission-gate'
import { useAuth } from '@/lib/contexts/auth-context'
import { PageHeader, FluentCard } from '@/components/layout/page-header'
import { getAvatarColor } from '@/lib/avatar-colors'

interface TenantOption {
  id: string
  name: string
}

export default function TeamsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tenantId = searchParams.get('tenantId')
  const { user: currentUser, hasPermission, hasAnyRole, isLoading: authLoading } = useAuth()

  // Check if user has admin access
  const canAccessPage = currentUser?.isSuperAdmin || hasAnyRole(['Admin', 'Tenant Admin']) || hasPermission('team.view')

  const [teams, setTeams] = useState<Team[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  // Company filter state
  const [availableCompanies, setAvailableCompanies] = useState<TenantOption[]>([])
  const [companiesLoading, setCompaniesLoading] = useState(false)

  // Check if current user is admin
  const isAdmin = currentUser?.isSuperAdmin || currentUser?.roles?.some(r =>
    r.name === 'Admin' || r.name === 'SuperAdmin' || r.name === 'System Admin'
  )

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Show access denied if user doesn't have permission
  if (!canAccessPage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <ShieldAlert className="h-12 w-12 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
        <p className="text-muted-foreground max-w-md mb-6">
          You don't have permission to access the Teams Management page. Please contact your administrator if you need access.
        </p>
        <Button variant="outline" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    )
  }

  // Handle company filter change
  const handleCompanyChange = (value: string) => {
    if (value === 'all') {
      searchParams.delete('tenantId')
    } else {
      searchParams.set('tenantId', value)
    }
    setSearchParams(searchParams)
  }

  // Load available companies for admin users
  useEffect(() => {
    const loadCompanies = async () => {
      if (!isAdmin) return
      try {
        setCompaniesLoading(true)
        const { tenantsService } = await import('@/lib/api/services/tenants')
        const response = await tenantsService.getTenants({ pageSize: 100, sortBy: 'name' })
        const companies = (response.items || []).map((t) => ({
          id: t.id,
          name: t.name
        }))
        setAvailableCompanies(companies)
      } catch {
        // Ignore error
      } finally {
        setCompaniesLoading(false)
      }
    }
    loadCompanies()
  }, [isAdmin])

  // Create/Edit dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingTeam, setEditingTeam] = useState<Team | null>(null)
  const [formData, setFormData] = useState({ name: '', description: '', color: '' })
  const [saving, setSaving] = useState(false)

  // Delete dialog state
  const [deleteTeam, setDeleteTeam] = useState<Team | null>(null)
  const [deleting, setDeleting] = useState(false)

  const {
    page,
    pageSize,
    sortField,
    sortDirection,
    filters,
    search,
    setPage,
    setPageSize,
    setSort,
    setFilters,
    setSearch,
  } = useDataTable({
    initialState: {
      pageSize: 20,
      sortField: 'name',
      sortDirection: 'asc',
    },
  })

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await teamService.getTeams(tenantId || undefined)
      setTeams(data)
      setTotalCount(data.length)
    } catch (_error) {
      setError('Failed to load teams')
      toast({
        title: 'Error',
        description: 'Failed to load teams',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [toast, tenantId])

  useEffect(() => {
    loadTeams()
  }, [loadTeams])

  const handleOpenCreate = () => {
    setEditingTeam(null)
    setFormData({ name: '', description: '', color: '' })
    setIsDialogOpen(true)
  }

  const handleOpenEdit = (team: Team) => {
    setEditingTeam(team)
    setFormData({
      name: team.name,
      description: team.description || '',
      color: team.color || '',
    })
    setIsDialogOpen(true)
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Error',
        description: 'Team name is required',
        variant: 'destructive',
      })
      return
    }

    setSaving(true)
    try {
      if (editingTeam) {
        await teamService.updateTeam(editingTeam.id, formData)
        toast({
          title: 'Success',
          description: 'Team updated successfully',
        })
      } else {
        await teamService.createTeam(formData)
        toast({
          title: 'Success',
          description: 'Team created successfully',
        })
      }
      setIsDialogOpen(false)
      loadTeams()
    } catch (_error) {
      toast({
        title: 'Error',
        description: editingTeam ? 'Failed to update team' : 'Failed to create team',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTeam) return

    setDeleting(true)
    try {
      await teamService.deleteTeam(deleteTeam.id)
      toast({
        title: 'Success',
        description: 'Team deleted successfully',
      })
      setDeleteTeam(null)
      loadTeams()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to delete team',
        variant: 'destructive',
      })
    } finally {
      setDeleting(false)
    }
  }

  const getTeamInitials = (team: Team) => {
    return team.name.substring(0, 2).toUpperCase()
  }

  // Filter teams based on search
  const filteredTeams = teams.filter((team) => {
    if (search) {
      const searchLower = search.toLowerCase()
      return (
        team.name.toLowerCase().includes(searchLower) ||
        team.description?.toLowerCase().includes(searchLower)
      )
    }
    return true
  })

  const columns: ColumnDef<Team>[] = [
    {
      id: 'name',
      header: 'Team',
      accessor: 'name',
      sortable: true,
      cell: (team) => {
        const color = getAvatarColor(team.id)
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9 rounded-full">
              <AvatarFallback
                className={`rounded-full font-semibold text-sm ${color.bg} ${color.text}`}
                style={team.color ? { backgroundColor: team.color } : undefined}
              >
                {getTeamInitials(team)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{team.name}</p>
              {team.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[300px]">
                  {team.description}
                </p>
              )}
            </div>
          </div>
        )
      },
    },
    {
      id: 'memberCount',
      header: 'Members',
      accessor: 'memberCount',
      sortable: true,
      cell: (team) => (
        <Badge variant="secondary" className="gap-1">
          <Users className="h-3 w-3" />
          {team.memberCount}
        </Badge>
      ),
    },
    {
      id: 'isActive',
      header: 'Status',
      accessor: 'isActive',
      sortable: true,
      cell: (team) => (
        <Badge variant={team.isActive ? 'success' : 'secondary'}>
          {team.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessor: 'createdAt',
      sortable: true,
      cell: (team) => (
        <span className="text-gray-500 dark:text-gray-400">
          {new Date(team.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: 'id',
      width: '150px',
      cell: (team) => (
        <TooltipProvider>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Eye className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View Team</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <UserPlus className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add Members</TooltipContent>
            </Tooltip>
            <PermissionGate permissions={['teams.update']}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleOpenEdit(team)}
                  >
                    <Pencil className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit Team</TooltipContent>
              </Tooltip>
            </PermissionGate>
            <PermissionGate permissions={['teams.delete']}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setDeleteTeam(team)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete Team</TooltipContent>
              </Tooltip>
            </PermissionGate>
          </div>
        </TooltipProvider>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Teams"
        description={tenantId
          ? `Teams for ${availableCompanies.find(c => c.id === tenantId)?.name || 'selected company'}`
          : "Manage teams and organize users into groups"
        }
        actions={
          <PermissionGate permissions={['teams.create']}>
            <Button
              onClick={handleOpenCreate}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors shadow-lg shadow-gray-900/20 dark:shadow-white/20"
            >
              <Plus className="h-4 w-4" />
              Create Team
            </Button>
          </PermissionGate>
        }
      />

      {/* Quick Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: 'Total Teams', value: totalCount, color: 'from-blue-400 to-blue-600' },
          { label: 'Active', value: teams.filter((t) => t.isActive).length, color: 'from-emerald-400 to-green-600' },
          { label: 'Total Members', value: teams.reduce((acc, t) => acc + t.memberCount, 0), color: 'from-violet-400 to-purple-600' },
        ].map((stat) => (
          <FluentCard key={stat.label} className="flex items-center gap-4">
            <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
            </div>
          </FluentCard>
        ))}
      </div>

      <FluentCard className="overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredTeams}
          totalCount={filteredTeams.length}
          loading={loading}
          error={error}
          onRetry={loadTeams}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={setSort}
          filters={filters}
          onFilterChange={setFilters}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search teams..."
          toolbarActions={
            isAdmin && availableCompanies.length > 0 ? (
              <Select
                value={tenantId || 'all'}
                onValueChange={handleCompanyChange}
                disabled={companiesLoading}
              >
                <SelectTrigger className="w-[200px] h-10">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {availableCompanies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : undefined
          }
          rowKey="id"
          emptyMessage="No teams found. Create your first team to get started."
        />
      </FluentCard>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTeam ? 'Edit Team' : 'Create Team'}</DialogTitle>
            <DialogDescription>
              {editingTeam
                ? 'Update the team details below.'
                : 'Create a new team to organize users.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Team Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter team name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter team description (optional)"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="color">Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="color"
                  type="color"
                  value={formData.color || '#0078D4'}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="#0078D4"
                  className="flex-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingTeam ? 'Save Changes' : 'Create Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTeam} onOpenChange={() => setDeleteTeam(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTeam?.name}"? This action cannot be undone
              and will remove all members from the team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
